import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { LanguageSelector } from "@components/LanguageSelector";

/**
 * Header component with responsive navigation
 *
 * Provides a responsive header with desktop navigation and mobile menu.
 * On mobile screens, displays a hamburger menu that opens an animated slide-in menu.
 * Includes language selector for English/Spanish switching.
 *
 * @returns {JSX.Element} The header component with responsive navigation
 */
export function Header() {
  const { t } = useTranslation();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isResourcesOpen, setIsResourcesOpen] = useState(false);
  const [isMobileResourcesOpen, setIsMobileResourcesOpen] = useState(false);

  /**
   * Toggles the mobile menu open/closed state
   */
  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  /**
   * Closes the mobile menu
   */
  const closeMobileMenu = () => {
    setIsMobileMenuOpen(false);
    setIsMobileResourcesOpen(false);
  };

  return (
    <header className="w-full py-6 px-6 md:px-10 relative">
      <div className="flex justify-between items-center">
        {/* Logo and Desktop Navigation - aligned left */}
        <div className="flex items-center gap-6">
          {/* Logo - fixed size to prevent stretching */}
          <a href="https://www.walrus.com" className="inline-block">
            <div className="flex-shrink-0">
              <img
                src="/images/walrus-logo.png"
                alt={t("common.logoAlt")}
                className="h-6 w-auto object-contain"
              />
            </div>
          </a>

          {/* Desktop Navigation - hidden on mobile */}
          <nav className="hidden md:flex gap-6 text-sm font-medium items-center relative">
            <a
              href="https://www.walrus.com"
              className="text-gray-700 hover:text-gray-900 transition-colors"
            >
              {t("header.home")}
            </a>

            {/* Member Resources dropdown */}
            <div className="relative">
              <button
                type="button"
                className="inline-flex items-center gap-1 text-gray-700 hover:text-gray-900 transition-colors"
                onClick={() => setIsResourcesOpen((o) => !o)}
              >
                {t("header.memberResources")}
              </button>

              {/* Dropdown panel */}
              {isResourcesOpen && (
                <div
                  className="absolute right-0 mt-2 w-auto rounded-xl bg-white shadow-lg border border-gray-100 py-2 z-50"
                  onMouseLeave={() => setIsResourcesOpen(false)}
                >
                  <a
                    href="https://www.walrus.com/swell"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 hover:text-gray-900"
                  >
                    {t("header.swell")}
                  </a>
                  <a
                    href="https://www.walrus.com/rewards-faq"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 hover:text-gray-900"
                  >
                    {t("header.rewardsFaq")}
                  </a>
                </div>
              )}
            </div>
          </nav>
        </div>

        {/* Desktop Buttons and Language Selector - hidden on mobile */}
        <div className="hidden md:flex gap-4 items-center">
          <LanguageSelector />
          <a href="https://member.walrus.com/" className="inline-block">
            <button className="px-4 py-2 rounded-full border border-gray-300 text-sm hover:border-gray-400 transition-colors">
              {t("header.memberLogin")}
            </button>
          </a>
          <a href="https://member.walrus.com/" className="inline-block">
            <button className="px-4 py-2 rounded-full bg-rose-600 text-white text-sm hover:bg-rose-700 transition-colors">
              {t("header.signUp")}
            </button>
          </a>
        </div>

        {/* Mobile Menu Button - visible only on mobile */}
        <button
          onClick={toggleMobileMenu}
          className="md:hidden flex flex-col justify-center items-center w-10 h-10 focus:outline-none"
          aria-label={t("header.toggleMenu")}
          aria-expanded={isMobileMenuOpen}
        >
          <span
            className={`block w-6 h-0.5 bg-gray-700 transition-all duration-300 ${
              isMobileMenuOpen ? "rotate-45 translate-y-1.5" : ""
            }`}
          />
          <span
            className={`block w-6 h-0.5 bg-gray-700 transition-all duration-300 mt-1.5 ${
              isMobileMenuOpen ? "opacity-0" : ""
            }`}
          />
          <span
            className={`block w-6 h-0.5 bg-gray-700 transition-all duration-300 mt-1.5 ${
              isMobileMenuOpen ? "-rotate-45 -translate-y-1.5" : ""
            }`}
          />
        </button>
      </div>

      {/* Mobile Menu Overlay */}
      <div
        className={`md:hidden fixed inset-0 bg-black bg-opacity-50 z-40 transition-opacity duration-300 ${
          isMobileMenuOpen ? "opacity-100" : "opacity-0 pointer-events-none"
        }`}
        onClick={closeMobileMenu}
      />

      {/* Mobile Menu Panel */}
      <div
        className={`md:hidden fixed top-0 right-0 bottom-0 w-64 bg-white shadow-2xl z-50 transform transition-transform duration-300 ease-in-out ${
          isMobileMenuOpen ? "translate-x-0" : "translate-x-full"
        }`}
      >
        <div className="flex flex-col h-full">
          {/* Close button */}
          <div className="flex justify-end p-6">
            <button
              onClick={closeMobileMenu}
              className="w-10 h-10 flex items-center justify-center rounded-full hover:bg-gray-100 transition-colors"
              aria-label={t("header.closeMenu")}
            >
              <svg
                className="w-6 h-6 text-gray-700"
                fill="none"
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {/* Navigation Links */}
          <nav className="flex flex-col px-6 space-y-4">
            <a
              href="https://www.walrus.com"
              className="text-gray-700 hover:text-gray-900 py-3 text-base font-medium border-b border-gray-100 transition-colors"
              onClick={closeMobileMenu}
            >
              {t("header.home")}
            </a>

            {/* Mobile Member Resources + sublinks */}
            <button
              type="button"
              className="flex items-center justify-between text-gray-700 hover:text-gray-900 py-3 text-base font-medium border-b border-gray-100 transition-colors"
              onClick={() => setIsMobileResourcesOpen((o) => !o)}
            >
              <span>{t("header.memberResources")}</span>
              <svg
                className={`w-4 h-4 transition-transform ${
                  isMobileResourcesOpen ? "rotate-180" : ""
                }`}
                viewBox="0 0 20 20"
                fill="none"
              >
                <path
                  d="M5 7l5 5 5-5"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </button>

            {isMobileResourcesOpen && (
              <div className="ml-2 mb-2 flex flex-col space-y-2 text-sm">
                <a
                  href="https://www.walrus.com/swell"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-gray-600 hover:text-gray-900 py-1"
                  onClick={closeMobileMenu}
                >
                  {t("header.swell")}
                </a>
                <a
                  href="https://www.walrus.com/rewards-faq"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-gray-600 hover:text-gray-900 py-1"
                  onClick={closeMobileMenu}
                >
                  {t("header.rewardsFaq")}
                </a>
              </div>
            )}
          </nav>

          {/* Buttons */}
          <div className="flex flex-col gap-3 px-6 mt-8">
            <a href="https://member.walrus.com/" className="inline-block">
              <button
                className="px-4 py-3 rounded-full border border-gray-300 text-sm font-medium hover:border-gray-400 transition-colors w-full"
                onClick={closeMobileMenu}
              >
                {t("header.memberLogin")}
              </button>
            </a>
            <a href="https://member.walrus.com/" className="inline-block">
              <button
                className="px-4 py-3 rounded-full bg-rose-600 text-white text-sm font-medium hover:bg-rose-700 transition-colors w-full"
                onClick={closeMobileMenu}
              >
                {t("header.signUp")}
              </button>
            </a>
          </div>

          {/* Mobile Language Selector */}
          <LanguageSelector isMobile onLanguageChange={closeMobileMenu} />
        </div>
      </div>
    </header>
  );
}
